import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { SkeletonBlock, SkeletonRegion } from '../../../Components/BrandPresenceSkeleton';
import { Form } from '@edgebox/react-components';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

export interface IFilterDropdownOption {
  id: string;
  label: string;
  /** A quieter second line under the label, such as the URL that tells two pages apart. */
  description?: string;
  badgeCount?: number;
  interactive?: boolean;
}

export interface IFilterDropdownOptionGroup {
  id: string;
  label: string;
  options: IFilterDropdownOption[];
}

/** An option's words: the label, and the quieter second line where the option carries one. */
function OptionLabel({ option }: { option: IFilterDropdownOption }) {
  return (
    <span className="min-w-0">
      <span className="block truncate">{option.label}</span>
      {option.description ? (
        <span className="block truncate font-eyebrow text-[0.6875rem] font-normal text-slate-400" title={option.description}>
          {option.description}
        </span>
      ) : null}
    </span>
  );
}

export function FilterDropdownList({
  search,
  onSearchChange,
  searchPlaceholder,
  selectedOptions,
  filteredOptions,
  groupedFilteredOptions,
  multiple,
  loading,
  onToggle,
  hideSelectedFromFiltered,
  onLoadMore,
  canLoadMore,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder: string;
  selectedOptions: IFilterDropdownOption[];
  filteredOptions: IFilterDropdownOption[];
  groupedFilteredOptions?: IFilterDropdownOptionGroup[];
  multiple: boolean;
  loading?: boolean;
  onToggle: (option: IFilterDropdownOption) => void;
  hideSelectedFromFiltered?: boolean;
  onLoadMore?: () => void;
  canLoadMore?: boolean;
}) {
  const searchInputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  const selectedIds = React.useMemo(() => new Set(selectedOptions.map((option) => option.id)), [selectedOptions]);
  const visibleOptions = React.useMemo(() => {
    if (!hideSelectedFromFiltered) {
      return filteredOptions;
    }

    return filteredOptions.filter((option) => !selectedIds.has(option.id));
  }, [filteredOptions, hideSelectedFromFiltered, selectedIds]);

  const visibleOptionIds = React.useMemo(() => new Set(visibleOptions.map((option) => option.id)), [visibleOptions]);

  const visibleGroups = React.useMemo(() => {
    if (!groupedFilteredOptions?.length) {
      return [];
    }

    return groupedFilteredOptions
      .map((group) => ({
        ...group,
        options: group.options.filter((option) => visibleOptionIds.has(option.id)),
      }))
      .filter((group) => group.options.length > 0);
  }, [groupedFilteredOptions, visibleOptionIds]);

  return (
    <div>
      <div className="relative mb-2">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
          <FontAwesomeIcon icon={faSearch} className="text-xs text-slate-400" />
        </span>
        <input
          ref={searchInputRef}
          className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50/70 pl-9 pr-3 text-sm placeholder:text-slate-400 transition-all duration-150 focus:border-primary focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary/10"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(event) => onSearchChange(event.target.value)}
        />
      </div>

      {selectedOptions.length ? (
        <>
          <div className="max-h-28 overflow-y-auto space-y-1 mb-2">
            {selectedOptions.map((option) => (
              <button
                key={`selected-${option.id}`}
                type="button"
                className="flex w-full items-center justify-between rounded-lg bg-primary/[0.07] px-2.5 py-1.5 text-left text-sm font-medium text-slate-800 transition-colors hover:bg-primary/[0.12]"
                onClick={() => onToggle(option)}
              >
                {multiple ? (
                  <span className="inline-flex items-center gap-2">
                    <Form.Check type="checkbox" className="mb-0" checked={true} readOnly />
                    <OptionLabel option={option} />
                  </span>
                ) : (
                  <OptionLabel option={option} />
                )}

                {!multiple ? (
                  <span className="inline-flex items-center gap-2 text-slate-500">
                    {typeof option.badgeCount === 'number' ? (
                      <span className="inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-[0.625rem] font-bold text-white tabular-nums">
                        {option.badgeCount}
                      </span>
                    ) : null}
                    {option.interactive ? <FontAwesomeIcon icon={faChevronRight} className="text-[10px]" /> : null}
                  </span>
                ) : null}
              </button>
            ))}
          </div>
          <hr className="my-2 border-slate-200/80" />
        </>
      ) : null}

      {loading ? (
        <SkeletonRegion label="Loading options" className="space-y-2 px-2.5 py-1.5">
          <SkeletonBlock className="h-4 w-3/4" />
          <SkeletonBlock className="h-4 w-1/2" />
          <SkeletonBlock className="h-4 w-2/3" />
        </SkeletonRegion>
      ) : null}

      <div className="max-h-64 overflow-y-auto space-y-1">
        {visibleGroups.length
          ? visibleGroups.map((group) => (
              <div key={group.id} className="pt-1">
                <div className="px-2.5 pb-1 pt-2 font-eyebrow text-[0.625rem] font-medium uppercase tracking-[0.14em] text-slate-500">
                  {group.label}
                </div>
                {group.options.map((option) => {
                  const isSelected = selectedOptions.some((entry) => entry.id === option.id);
                  return (
                    <button
                      key={option.id}
                      type="button"
                      className="flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100/80 hover:text-slate-900"
                      onClick={() => onToggle(option)}
                    >
                      {multiple ? (
                        <span className="inline-flex items-center gap-2">
                          <Form.Check type="checkbox" className="mb-0" checked={isSelected} readOnly />
                          <OptionLabel option={option} />
                        </span>
                      ) : (
                        <OptionLabel option={option} />
                      )}

                      {!multiple ? (
                        <span className="inline-flex items-center gap-2 text-slate-500">
                          {typeof option.badgeCount === 'number' ? (
                            <span className="inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-[0.625rem] font-bold text-white tabular-nums">
                              {option.badgeCount}
                            </span>
                          ) : null}
                          {option.interactive ? <FontAwesomeIcon icon={faChevronRight} className="text-[10px]" /> : null}
                        </span>
                      ) : null}
                    </button>
                  );
                })}
              </div>
            ))
          : visibleOptions.map((option) => {
              const isSelected = selectedOptions.some((entry) => entry.id === option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  className="flex w-full items-center justify-between rounded-lg px-2.5 py-1.5 text-left text-sm text-slate-700 transition-colors hover:bg-slate-100/80 hover:text-slate-900"
                  onClick={() => onToggle(option)}
                >
                  {multiple ? (
                    <span className="inline-flex items-center gap-2">
                      <Form.Check type="checkbox" className="mb-0" checked={isSelected} readOnly />
                      <OptionLabel option={option} />
                    </span>
                  ) : (
                    <OptionLabel option={option} />
                  )}

                  {!multiple ? (
                    <span className="inline-flex items-center gap-2 text-slate-500">
                      {typeof option.badgeCount === 'number' ? (
                        <span className="inline-flex items-center rounded-full bg-primary px-2 py-0.5 text-[0.625rem] font-bold text-white tabular-nums">
                          {option.badgeCount}
                        </span>
                      ) : null}
                      {option.interactive ? <FontAwesomeIcon icon={faChevronRight} className="text-[10px]" /> : null}
                    </span>
                  ) : null}
                </button>
              );
            })}
      </div>

      {canLoadMore && onLoadMore ? (
        <button
          type="button"
          className="mt-2 w-full rounded-lg py-1.5 text-sm font-semibold text-primary transition-colors hover:bg-primary/[0.07]"
          onClick={onLoadMore}
        >
          Load more
        </button>
      ) : null}
    </div>
  );
}
